/**
 * config/level.ts — Which level the game is currently playing.
 *
 * `constants.ts` is the single source of truth for every value that is the same
 * on every level — how stiff the leash is, what clay looks like, how a cottage
 * is built. This file holds the values that are **not**: the shape of the town
 * and the pressure applied to the player inside it. Everything that reads the
 * layout — `Game`, the two managers, the render layer — asks `activeLevel()`
 * rather than reaching into `constants.ts`, and that indirection is the whole
 * mechanism by which a generated town becomes playable.
 *
 * ## The default is the hand-authored town
 *
 * `authoredLevel()` is the town that used to live in `BUILDINGS.LIST`,
 * `TREES.LIST`, `ROADS.SEGMENTS`, `GROUND.PARKS` and `OBJECTIVE.BIN`/`HOME`,
 * read back out as a `LevelSpec`. Change nothing and the game plays exactly the
 * level it always has, which is the point: the generator is an addition, not a
 * replacement, and the authored town remains the reference every generated one
 * is measured against (`difficulty.test.ts` pins it to the medium band).
 *
 * ## Why this is module state
 *
 * A level is chosen once, at boot, and then read by nearly every system in the
 * game on nearly every frame. Threading it through as a parameter would mean
 * touching every constructor between `main.ts` and the facade builder to carry
 * something that never changes during a run. `setActiveLevel` is deliberately
 * blunt about the cost: it must be called **before** a `Game` is constructed,
 * because the world is built in the constructor.
 */
import {
  BUILDINGS,
  GROUND,
  HAZARD,
  OBJECTIVE,
  PAVEMENT,
  ROADS,
  TRAFFIC,
  TREES,
  VEHICLES,
  WORLD,
} from './constants';
import { homeDoorstep } from '../systems/home';
import { footwayBands } from '../systems/pavement';
import { spansMap } from '../systems/roads';
import { rateLevel } from '../systems/levelgen/difficulty';
import type { Rect } from '../systems/level';
import type {
  CritterRelease,
  LevelSpec,
  PlotSpec,
  RoadSpec,
  ToneSpec,
  TreeSpec,
  VehicleRelease,
} from '../systems/levelgen/types';
import type { HazardType, TreeKind } from '../types';

/**
 * The hand-authored town, as a level.
 *
 * Built fresh on each call rather than shared, so a caller mutating what it
 * gets back can't corrupt the default for the next run.
 */
export function authoredLevel(): LevelSpec {
  const home = {
    x: OBJECTIVE.HOME.X - OBJECTIVE.HOME.WIDTH / 2,
    y: OBJECTIVE.HOME.Y - OBJECTIVE.HOME.HEIGHT / 2,
    w: OBJECTIVE.HOME.WIDTH,
    h: OBJECTIVE.HOME.HEIGHT,
  };
  const spec = {
    seed: 'authored',
    world: { width: WORLD.WIDTH, height: WORLD.HEIGHT },
    // The authored town has no surround: it was drawn to be walked to its own
    // edges, and every number in it — the road positions, the footway
    // clearances in `BUILDINGS.LIST` — is measured against that rectangle. So
    // the playable ground *is* the map, which is what a level without a
    // generated ring means, and what keeps this town the unchanged reference
    // every generated one is rated against.
    bounds: { x: 0, y: 0, w: WORLD.WIDTH, h: WORLD.HEIGHT },
    roads: ROADS.SEGMENTS.map(
      (r): RoadSpec => ({
        orient: r.orient as 'h' | 'v',
        // Spread rather than listed field by field: dropping `from`/`to`/`kind`
        // here would silently turn the close and the alley back into ordinary
        // streets running the whole map, and the town would still build.
        ...('from' in r ? { from: r.from } : {}),
        ...('to' in r ? { to: r.to } : {}),
        ...('kind' in r ? { kind: r.kind as 'street' | 'lane' } : {}),
        pos: r.pos,
      }),
    ),
    buildings: BUILDINGS.LIST.map(
      (b): PlotSpec => ({
        x: b.x,
        y: b.y,
        w: b.w,
        h: b.h,
        tall: b.tall,
        patio: b.patio,
        tones: b.tones.map(
          (t): ToneSpec => ({
            color: t.color,
            roofColor: t.roofColor,
            paint: t.paint,
            material: t.material as ToneSpec['material'],
          }),
        ),
      }),
    ),
    parks: GROUND.PARKS.map((p) => ({ x: p.x, y: p.y, w: p.w, h: p.h })),
    trees: TREES.LIST.map(
      (t): TreeSpec => ({ x: t.x, y: t.y, scale: t.scale, kind: t.kind as TreeKind }),
    ),
    // One bin, where it has always stood. A generated town gets several (see
    // `levelgen/landmarks.ts` → `placeBins`) because finding the only bin on a
    // map you have never seen is a search rather than a walk; this town is
    // hand-made, small, and the reference every generated one is rated against,
    // so it keeps the single bin its difficulty was calibrated on.
    bins: [{ x: OBJECTIVE.BIN.X, y: OBJECTIVE.BIN.Y }],
    home,
    // On the doormat, like every level: a walk is a round trip from home.
    // (The walker used to start in the middle of the map, which meant the win
    // zone was a place they had simply left rather than come back to.)
    start: homeDoorstep(home),
    traffic: authoredTraffic(),
    hazards: authoredHazards(),
  };

  // Rated rather than declared: the authored town gets its label from the same
  // scale as every generated one, which is what makes the scale worth anything.
  const rating = rateLevel(spec);
  return { ...spec, difficulty: rating.difficulty, rating };
}

/**
 * The authored town's traffic, written out as a schedule.
 *
 * The live game spawns on a random timer (`TRAFFIC.SPAWN_MIN_MS` plus jitter);
 * this lays that same cadence out at its **mean** gap so the level has a
 * schedule like any other. It is not a behaviour change for the authored town —
 * `TrafficManager` still jitters around what the schedule asks for (see its
 * note on `jitter`) — it is what lets one code path serve both, and what lets
 * the authored level be rated on the same scale as a generated one.
 */
function authoredTraffic(): LevelSpec['traffic'] {
  const gap = TRAFFIC.SPAWN_MIN_MS + TRAFFIC.SPAWN_JITTER_MS / 2;
  const span = 90000;
  const schedule: VehicleRelease[] = [];

  // Only the ways that run clean across the map. Derived rather than listed,
  // so adding a close or an alley to `ROADS.SEGMENTS` cannot put a car on it —
  // which would be a run ended by something the player could not have seen.
  const world = { width: WORLD.WIDTH, height: WORLD.HEIGHT };
  const drivable = ROADS.SEGMENTS.flatMap((r, i) =>
    spansMap({ ...r, orient: r.orient as 'h' | 'v' }, world) ? [i] : [],
  );

  for (let i = 0, at = TRAFFIC.FIRST_SPAWN_MS; at < span; i++, at += gap) {
    schedule.push({
      atMs: at,
      road: drivable[i % drivable.length],
      dir: i % 2 === 0 ? 1 : -1,
      // A third of them vans, matching TRAFFIC.VAN_SHARE closely enough that
      // the rating sees the same mix the player does.
      kind: i % 3 === 0 ? 'van' : 'car',
    });
  }
  return {
    maxVehicles: TRAFFIC.MAX_VEHICLES,
    speed: VEHICLES.SPEED,
    schedule,
    loopMs: span,
  };
}

/**
 * The authored town's wildlife.
 *
 * Positions were never written down — `HazardManager` rolled them at
 * construction — so they stay unwritten here: inventing coordinates would be
 * describing a level nobody played. The counts from `HAZARD.COUNT` are listed
 * with no position at all, which `CritterRelease` defines to mean "scatter it",
 * and the manager does exactly what it always did.
 */
function authoredHazards(): LevelSpec['hazards'] {
  const spawns: CritterRelease[] = [];
  for (const type of Object.keys(HAZARD.COUNT) as HazardType[]) {
    for (let i = 0; i < HAZARD.COUNT[type]; i++) spawns.push({ type, atMs: 0 });
  }
  return {
    spawns,
    speedMin: HAZARD.SPEED_MIN,
    speedMax: HAZARD.SPEED_MAX,
    retargetMinMs: HAZARD.RETARGET_MIN_MS,
    retargetJitterMs: HAZARD.RETARGET_JITTER_MS,
  };
}

let current: LevelSpec = authoredLevel();
let footways: Rect[] | null = null;

/** The level being played. Safe to call from anywhere, on any frame. */
export function activeLevel(): LevelSpec {
  return current;
}

/**
 * Every footway on the level being played.
 *
 * Derived once per level rather than per caller. Three separate places need it
 * to decide which way a building fronts — `terrace.ts` cuts the row that way,
 * `buildings.ts` hangs the door and the patio that way, and `world.ts` keeps the
 * weeds off the front path that way — and three copies of a derivation is how
 * they drift apart. It is also not free: `footwayBands` walks every road twice,
 * and `frontFacing` is asked about every plot in the town.
 *
 * The generator deliberately does **not** use this. `levelgen/validate.ts`
 * re-derives the bands from the spec it is checking, because a validator that
 * shares the placer's working cannot catch the placer being wrong.
 */
export function activeFootways(): Rect[] {
  footways ??= footwayBands(current.world, current.roads, ROADS.WIDTH, PAVEMENT.WIDTH);
  return footways;
}

/**
 * Swap in a different level.
 *
 * **Call this before constructing a `Game`.** The town is built in the `Game`
 * constructor, so a level swapped in mid-run changes the collision rects and
 * the objectives without changing a single mesh — the walker would be standing
 * in a house that isn't drawn.
 */
export function setActiveLevel(level: LevelSpec): void {
  current = level;
  footways = null;
}

/** Go back to the hand-authored town. Mostly for tests, which must not leak. */
export function resetActiveLevel(): void {
  current = authoredLevel();
  footways = null;
}
